/**
 * The hero "comp lens" animation — pure TypeScript, NO React import, per the implementation
 * guide's own stated rationale (§1 "Rule"): keeps the physics unit-testable and reusable outside
 * the landing page later. `HeroCanvas.tsx` only mounts/unmounts this.
 *
 * Ported MECHANICALLY from the approved prototype (`valubrieflanding_2.html`'s inline <script>),
 * which is the behavioral spec — every numeric constant, timing, and code path below is carried
 * over unchanged unless a comment marks it as an intentional production deviation.
 *
 * Intentional production deviations from the prototype (per the implementation guide §6.2):
 *  1. Real data: `record(n)`'s synthetic price generator is REMOVED. Each parcel node is assigned
 *     a row from the `comps` argument (seeded-shuffled so the assignment is stable across a
 *     session), never a fabricated number.
 *  2. FLAGGED, project-strict-tier-driven addition NOT in the implementation guide: if `comps` is
 *     empty (e.g. the `landing_comps` table has not been seeded yet in this environment — see
 *     `lib/landingApi.ts`), the engine does NOT fall back to any hard-coded/synthetic price array.
 *     The guide's own §4 suggests "a small hard-coded array clearly labeled in code as fallback";
 *     this build declines that specific fallback because a visitor cannot see code comments — a
 *     labeled-in-code fake number is still an invented number on the rendered page, which is
 *     exactly what the project's strict-tier anti-hallucination rule forbids, even for marketing
 *     decoration. Instead: the map, lens, ambient network, ripples, and pin marker/ring all render
 *     normally (they are geometry, not data); only the numeric price labels and the "N COMPS IN
 *     LENS / SUPPORTED RANGE" readout are omitted when there is no real row to back them.
 *  3. Token-driven colors: reads `canvasColorTriplets` from `./tokens.ts` instead of hard-coded
 *     rgba strings (same intent as the guide's `getComputedStyle(--canvas-*)` approach, adapted to
 *     a component-scoped React app rather than a global Next.js stylesheet).
 */
import { canvasPaletteFor, type CanvasPalette } from "./tokens";

export type LandingComp = { priceK: number; beds: number; sqft: number };

type Node = {
  hx: number;
  hy: number;
  x: number;
  y: number;
  px: number;
  py: number;
  comp: LandingComp | null;
};

type Pin = { x: number; y: number; t0: number };
type Ripple = { x: number; y: number; t0: number };

export interface CompLensHandle {
  /** Cancels the rAF loop, disconnects the IntersectionObserver, and removes every listener this
   * engine attached. Must be safe to call more than once (React StrictMode double-invokes
   * effects in dev — a leaky engine here would double-animate, which the implementation guide
   * itself calls out as a failing-test condition). */
  destroy: () => void;
}

const LINK = 140;
const LENS = 165;
const PIN_R = 190;

/** mulberry32 — fixed-seed PRNG so every visitor sees the identical procedural map (and, given
 * the same `comps` array, the identical node/comp assignment). Ported verbatim. */
function mulberry32(seed: number): () => number {
  let s = seed;
  return function () {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let z = Math.imul(s ^ (s >>> 15), 1 | s);
    z = (z + Math.imul(z ^ (z >>> 7), 61 | z)) ^ z;
    return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
  };
}

/** Pure pin-lifecycle alpha curve as a function of "seconds since pin.t0" — hoisted to module
 * scope (out of the closure) specifically so it is independently unit-testable per the
 * implementation guide §6.2 ("pin lifecycle alpha at t = 0 / 0.1 / 1.0 / 1.5 / 1.8"). Ported
 * verbatim from the prototype's inline fade-in/hold/fade-out thresholds. */
export function pinAlphaAt(age: number): number {
  if (age < 0.15) return age / 0.15;
  if (age < 1.3) return 1;
  if (age < 1.75) return 1 - (age - 1.3) / 0.45;
  return 0;
}

/** Pure nearest-K selection by Euclidean distance within `maxDist` — hoisted for the same
 * unit-testability reason ("nearest-6 comp selection"). */
export function nearestK<T extends { x: number; y: number }>(
  px: number,
  py: number,
  candidates: T[],
  k: number,
  maxDist: number
): { item: T; d: number }[] {
  return candidates
    .map((item) => ({ item, d: Math.hypot(px - item.x, py - item.y) }))
    .filter((c) => c.d < maxDist)
    .sort((a, b) => a.d - b.d)
    .slice(0, k);
}

/** Pure ±6% supported-range math over a set of priceK values — hoisted for unit-testability
 * ("supported-range math against known inputs"). Mirrors the prototype's `avg*0.94`/`avg*1.06`. */
export function supportedRangeFromPrices(pricesK: number[]): { avg: number; lo: number; hi: number } | null {
  if (!pricesK.length) return null;
  const sum = pricesK.reduce((acc, p) => acc + p, 0);
  const avg = Math.round(sum / pricesK.length);
  return { avg, lo: Math.round(avg * 0.94), hi: Math.round(avg * 1.06) };
}

export function createCompLens(
  canvas: HTMLCanvasElement,
  opts: { comps: LandingComp[]; dark?: boolean },
): CompLensHandle {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    // No 2D context (e.g. an exotic test environment) — return a no-op handle rather than throw;
    // the hero copy/CTAs still render without the canvas (SSR-fallback-equivalent behavior).
    return { destroy: () => {} };
  }

  /* THE PALETTE IS RESOLVED ONCE, HERE, AND NEVER READ FROM THE DOM (A-MARKETING, 2026-09-01).
     The caller decides the scheme and re-creates the engine when it changes — `HeroCanvas.tsx`
     watches both `data-theme` and `prefers-color-scheme` and passes the answer down. Reading
     `getComputedStyle` from inside a `requestAnimationFrame` loop would be the same information
     bought at sixty layout reads a second, and the static map is baked once into an offscreen
     canvas anyway: nothing in here can respond to a colour change without being rebuilt.
     `dark` is OPTIONAL and defaults to the light palette, so every existing caller and every test
     that constructs this engine keeps the exact colours it had. */
  const P: CanvasPalette = canvasPaletteFor(!!opts.dark);

  const reduced =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
  const coarse =
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(pointer: coarse)").matches : false;

  let W = 0,
    H = 0,
    dpr = 1;
  let map: HTMLCanvasElement | null = null;
  let nodes: Node[] = [];
  let intersections: { x: number; y: number }[] = [];
  const lens = { x: -9999, y: -9999 };
  let pin: Pin | null = null;
  let ripples: Ripple[] = [];
  let t = 0;
  let raf: number | null = null;
  let visible = true;
  let reducedTimer: ReturnType<typeof setTimeout> | undefined;
  let destroyed = false;

  function buildMap() {
    const m = document.createElement("canvas");
    m.width = Math.round(W * dpr);
    m.height = Math.round(H * dpr);
    const mc = m.getContext("2d");
    if (!mc) {
      map = m;
      return;
    }
    mc.setTransform(dpr, 0, 0, dpr, 0, 0);
    const rnd = mulberry32(20260726);

    mc.fillStyle = P.ground;
    mc.fillRect(0, 0, W, H);

    const vx: number[] = [];
    const hy: number[] = [];
    let x = -30;
    while (x < W + 60) {
      x += 64 + rnd() * 58;
      vx.push(x + (rnd() - 0.5) * 10);
    }
    let y = -30;
    while (y < H + 60) {
      y += 60 + rnd() * 54;
      hy.push(y + (rnd() - 0.5) * 10);
    }

    for (let i = 0; i < vx.length - 1; i++) {
      for (let j = 0; j < hy.length - 1; j++) {
        if (rnd() < 0.16) {
          mc.fillStyle = rnd() < 0.5 ? P.parcelA : P.parcelB;
          mc.fillRect(vx[i], hy[j], vx[i + 1] - vx[i], hy[j + 1] - hy[j]);
        }
      }
    }

    for (let p = 0; p < 4; p++) {
      const pi = 1 + Math.floor(rnd() * (vx.length - 3));
      const pj = 1 + Math.floor(rnd() * (hy.length - 3));
      mc.fillStyle = `rgba(${P.node}, 0.075)`;
      mc.fillRect(vx[pi] + 3, hy[pj] + 3, vx[pi + 1] - vx[pi] - 6, hy[pj + 1] - hy[pj] - 6);
      mc.strokeStyle = `rgba(${P.node}, 0.14)`;
      mc.lineWidth = 1;
      mc.strokeRect(vx[pi] + 3.5, hy[pj] + 3.5, vx[pi + 1] - vx[pi] - 7, hy[pj + 1] - hy[pj] - 7);
    }

    mc.save();
    mc.beginPath();
    const rx = W * 0.8;
    mc.moveTo(rx, -20);
    mc.bezierCurveTo(rx - 60, H * 0.28, rx + 90, H * 0.62, rx + 10, H + 20);
    mc.lineWidth = 30;
    mc.strokeStyle = `rgba(${P.water}, 0.16)`;
    mc.lineCap = "round";
    mc.stroke();
    mc.lineWidth = 26;
    mc.strokeStyle = `rgba(${P.waterLight}, 0.13)`;
    mc.stroke();
    mc.restore();

    mc.strokeStyle = `rgba(${P.ink}, 0.075)`;
    mc.lineWidth = 1;
    vx.forEach((X) => {
      mc.beginPath();
      mc.moveTo(X, 0);
      mc.lineTo(X + (rnd() - 0.5) * 14, H);
      mc.stroke();
    });
    hy.forEach((Y) => {
      mc.beginPath();
      mc.moveTo(0, Y);
      mc.lineTo(W, Y + (rnd() - 0.5) * 14);
      mc.stroke();
    });

    for (let s = 0; s < 26; s++) {
      const sx = rnd() * W,
        sy = rnd() * H,
        len = 14 + rnd() * 26,
        horiz = rnd() < 0.5;
      mc.beginPath();
      mc.moveTo(sx, sy);
      mc.lineTo(sx + (horiz ? len : 0), sy + (horiz ? 0 : len));
      mc.stroke();
      if (rnd() < 0.5) {
        mc.beginPath();
        mc.arc(sx + (horiz ? len : 0), sy + (horiz ? 0 : len), 4.5, 0, Math.PI * 2);
        mc.stroke();
      }
    }

    function artery(x1: number, y1: number, x2: number, y2: number) {
      mc!.strokeStyle = `rgba(${P.ink}, 0.05)`;
      mc!.lineWidth = 7;
      mc!.beginPath();
      mc!.moveTo(x1, y1);
      mc!.lineTo(x2, y2);
      mc!.stroke();
      mc!.strokeStyle = `rgba(${P.ink}, 0.16)`;
      mc!.lineWidth = 2.4;
      mc!.beginPath();
      mc!.moveTo(x1, y1);
      mc!.lineTo(x2, y2);
      mc!.stroke();
      mc!.strokeStyle = P.dash;
      mc!.lineWidth = 0.8;
      mc!.setLineDash([9, 9]);
      mc!.beginPath();
      mc!.moveTo(x1, y1);
      mc!.lineTo(x2, y2);
      mc!.stroke();
      mc!.setLineDash([]);
    }
    for (let a = 3; a < vx.length; a += 4) artery(vx[a], 0, vx[a] + (rnd() - 0.5) * 20, H);
    for (let b = 3; b < hy.length; b += 4) artery(0, hy[b], W, hy[b] + (rnd() - 0.5) * 20);

    mc.save();
    mc.strokeStyle = `rgba(${P.ink}, 0.045)`;
    mc.lineWidth = 10;
    mc.lineCap = "round";
    mc.beginPath();
    mc.moveTo(W * 0.06, H * 1.05);
    mc.quadraticCurveTo(W * 0.5, H * 0.42, W * 1.02, H * 0.1);
    mc.stroke();
    mc.strokeStyle = `rgba(${P.node}, 0.20)`;
    mc.lineWidth = 2.6;
    mc.beginPath();
    mc.moveTo(W * 0.06, H * 1.05);
    mc.quadraticCurveTo(W * 0.5, H * 0.42, W * 1.02, H * 0.1);
    mc.stroke();
    mc.restore();

    mc.font = '500 8.5px "IBM Plex Mono", monospace';
    mc.fillStyle = `rgba(${P.ink}, 0.28)`;
    const names = [
      "CEDAR CT",
      "ELM ST",
      "OAK LN",
      "CRESTMOOR DR",
      "WINE BARREL LN",
      "REMBRANDT DR",
      "CUSTER RD",
      "STONEBRIDGE DR",
    ];
    for (let nmi = 0; nmi < names.length; nmi++) {
      const vert = rnd() < 0.45;
      if (vert) {
        const lx = vx[Math.floor(rnd() * vx.length)];
        const ly = H * (0.15 + rnd() * 0.7);
        mc.save();
        mc.translate(lx - 3, ly);
        mc.rotate(-Math.PI / 2);
        mc.fillText(names[nmi], 0, 0);
        mc.restore();
      } else {
        const lx = W * (0.1 + rnd() * 0.75);
        const ly = hy[Math.floor(rnd() * hy.length)];
        mc.fillText(names[nmi], lx, ly - 4);
      }
    }

    intersections = [];
    for (let ii = 0; ii < vx.length; ii++) {
      for (let jj = 0; jj < hy.length; jj++) {
        if (vx[ii] > -10 && vx[ii] < W + 10 && hy[jj] > -10 && hy[jj] < H + 10) {
          intersections.push({ x: vx[ii], y: hy[jj] });
        }
      }
    }

    map = m;
  }

  function buildNodes() {
    let target = Math.round((W * H) / 15000);
    target = Math.max(30, Math.min(coarse ? 46 : 92, target));
    nodes = [];
    const rnd = mulberry32(4451);

    const comps = opts.comps;
    // Deviation #1 (see file docstring): seeded-shuffle the real comps so assignment is stable
    // across a session but not in file order. Empty `comps` -> every node's `.comp` stays null,
    // which downstream rendering treats as "no label," never a fabricated one.
    const shuffled: LandingComp[] = comps.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    for (let i = 0; i < target; i++) {
      const base = intersections[Math.floor(rnd() * intersections.length)] || { x: rnd() * W, y: rnd() * H };
      const hx = base.x + (rnd() - 0.5) * 44;
      const hyy = base.y + (rnd() - 0.5) * 40;
      nodes.push({
        hx,
        hy: hyy,
        x: hx,
        y: hyy,
        px: rnd() * Math.PI * 2,
        py: rnd() * Math.PI * 2,
        comp: shuffled.length ? shuffled[i % shuffled.length] : null,
      });
    }
  }

  function recordLabel(n: Node): { short: string; line: string } | null {
    if (!n.comp) return null;
    const { priceK, beds, sqft } = n.comp;
    return { short: `$${priceK}K`, line: `$${priceK}K · ${beds}bd · ${sqft.toLocaleString()} SF` };
  }

  function softText(str: string, x: number, y: number, size: number, color: string, align?: CanvasTextAlign) {
    ctx!.save();
    ctx!.font = `500 ${size}px "IBM Plex Mono", monospace`;
    ctx!.textAlign = align || "left";
    ctx!.shadowColor = `rgba(${P.halo}, 0.95)`;
    ctx!.shadowBlur = 6;
    ctx!.fillStyle = color;
    ctx!.fillText(str, x, y);
    ctx!.fillText(str, x, y);
    ctx!.restore();
  }

  function nearestComps(px: number, py: number, candidates: Node[], k: number): { n: Node; d: number }[] {
    return nearestK(px, py, candidates, k, PIN_R).map(({ item, d }) => ({ n: item, d }));
  }

  function supportedRange(comps: { n: Node }[]): { avg: number; lo: number; hi: number } | null {
    const priced = comps.map((c) => c.n.comp).filter((c): c is LandingComp => !!c);
    return supportedRangeFromPrices(priced.map((c) => c.priceK));
  }

  function revealCircle(x: number, y: number, r: number, strength: number) {
    const g = ctx!.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0.0, `rgba(0,0,0,${0.95 * strength})`);
    g.addColorStop(0.55, `rgba(0,0,0,${0.7 * strength})`);
    g.addColorStop(0.85, `rgba(0,0,0,${0.18 * strength})`);
    g.addColorStop(1.0, "rgba(0,0,0,0)");
    ctx!.fillStyle = g;
    ctx!.beginPath();
    ctx!.arc(x, y, r, 0, Math.PI * 2);
    ctx!.fill();
  }

  function frame() {
    t += 0.006;
    ctx!.clearRect(0, 0, W, H);

    lens.x = W * (0.52 + 0.34 * Math.sin(t * 0.72) * Math.cos(t * 0.28));
    lens.y = H * (0.48 + 0.3 * Math.sin(t * 0.47 + 1.3));

    let pinA = 0;
    if (pin) {
      const pAge = t - pin.t0;
      pinA = pinAlphaAt(pAge);
      if (pAge >= 1.75) pin = null;
    }

    revealCircle(lens.x, lens.y, LENS + 70, 1);
    if (pin) revealCircle(pin.x, pin.y, PIN_R + 50, pinA);
    if (map) {
      ctx!.globalCompositeOperation = "source-in";
      ctx!.drawImage(map, 0, 0, W, H);
      ctx!.globalCompositeOperation = "source-over";
    }

    const focusPt = pin || lens;
    const focusR = pin ? PIN_R : LENS;

    for (const n of nodes) {
      n.x = n.hx + Math.sin(t * 0.9 + n.px) * 7 + Math.sin(t * 0.31 + n.py) * 4;
      n.y = n.hy + Math.cos(t * 0.8 + n.py) * 7 + Math.cos(t * 0.27 + n.px) * 4;
    }

    for (let i = 0; i < nodes.length; i++) {
      for (let k = i + 1; k < nodes.length; k++) {
        const a = nodes[i],
          b = nodes[k];
        const dx = a.x - b.x,
          dy = a.y - b.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > LINK) continue;
        const fade = 1 - d / LINK;
        const mx = (a.x + b.x) / 2,
          my = (a.y + b.y) / 2;
        const inF = Math.hypot(focusPt.x - mx, focusPt.y - my) < focusR;
        const fA = pin ? pinA : 1;
        ctx!.strokeStyle = inF
          ? `rgba(${P.node}, ${(fade * (0.1 + 0.32 * fA)).toFixed(3)})`
          : `rgba(${P.node}, ${(fade * 0.1).toFixed(3)})`;
        ctx!.lineWidth = inF ? 0.8 + 0.35 * fA : 0.8;
        ctx!.beginPath();
        ctx!.moveTo(a.x, a.y);
        ctx!.lineTo(b.x, b.y);
        ctx!.stroke();
      }
    }

    let pinComps: { n: Node; d: number }[] = [];
    if (pin) {
      pinComps = nearestComps(pin.x, pin.y, nodes, 6);
      for (let i = 0; i < pinComps.length; i++) {
        ctx!.strokeStyle = `rgba(${P.node}, ${((0.55 - i * 0.06) * pinA).toFixed(3)})`;
        ctx!.lineWidth = 1.5;
        ctx!.beginPath();
        ctx!.moveTo(pin.x, pin.y);
        ctx!.lineTo(pinComps[i].n.x, pinComps[i].n.y);
        ctx!.stroke();
      }
    }

    let focus: Node | null = null,
      focusD = 1e9;
    for (const n of nodes) {
      const dl = Math.hypot(focusPt.x - n.x, focusPt.y - n.y);
      const hot = dl < focusR;
      if (!pin && dl < focusD) {
        focusD = dl;
        focus = n;
      }
      const hA = pin ? pinA : 1;
      ctx!.beginPath();
      ctx!.arc(n.x, n.y, hot ? 1.8 + 0.8 * hA : 1.8, 0, Math.PI * 2);
      ctx!.fillStyle = hot
        ? `rgba(${P.node}, ${(0.3 + (0.15 + 0.45 * (1 - dl / focusR)) * hA).toFixed(3)})`
        : `rgba(${P.node}, 0.30)`;
      ctx!.fill();
    }

    if (!pin && focus && focusD < 85) {
      const rec = recordLabel(focus);
      if (rec) softText(rec.line, focus.x + 11, focus.y - 9, 11, `rgba(${P.label}, 0.85)`);
    }

    if (pin) {
      ctx!.save();
      ctx!.globalAlpha = pinA;
      const age = t - pin.t0;
      ctx!.beginPath();
      ctx!.arc(pin.x, pin.y, PIN_R * Math.min(1, age * 2.2), 0, Math.PI * 2);
      ctx!.strokeStyle = `rgba(${P.node}, 0.22)`;
      ctx!.lineWidth = 1;
      ctx!.setLineDash([4, 6]);
      ctx!.stroke();
      ctx!.setLineDash([]);

      ctx!.beginPath();
      ctx!.arc(pin.x, pin.y, 5.5, 0, Math.PI * 2);
      ctx!.fillStyle = P.pin;
      ctx!.fill();
      ctx!.beginPath();
      ctx!.arc(pin.x, pin.y, 9.5 + Math.sin(t * 3) * 1.2, 0, Math.PI * 2);
      ctx!.strokeStyle = `rgba(${P.node}, 0.55)`;
      ctx!.lineWidth = 1.6;
      ctx!.stroke();

      softText("SUBJECT", pin.x + 15, pin.y - 12, 10, `rgba(${P.label}, 0.9)`);

      const range = supportedRange(pinComps);
      for (let i = 0; i < pinComps.length; i++) {
        const rr = recordLabel(pinComps[i].n);
        if (rr) softText(rr.short, pinComps[i].n.x + 8, pinComps[i].n.y - 7, 10, `rgba(${P.label}, 0.72)`);
      }
      if (range) {
        softText(`${pinComps.length} COMPS IN LENS`, pin.x + 15, pin.y + 8, 10, `rgba(${P.label}, 0.75)`);
        softText(
          `SUPPORTED RANGE $${range.lo}K–$${range.hi}K`,
          pin.x + 15,
          pin.y + 24,
          10,
          `rgba(${P.label}, 0.9)`
        );
      }
      ctx!.restore();
    }

    for (let i = ripples.length - 1; i >= 0; i--) {
      const rp = ripples[i];
      const ra = (t - rp.t0) * 3.2;
      if (ra > 1) {
        ripples.splice(i, 1);
        continue;
      }
      ctx!.beginPath();
      ctx!.arc(rp.x, rp.y, ra * 120, 0, Math.PI * 2);
      ctx!.strokeStyle = `rgba(${P.node}, ${((1 - ra) * 0.4).toFixed(3)})`;
      ctx!.lineWidth = 1.6;
      ctx!.stroke();
    }

    if (!reduced && !destroyed) raf = requestAnimationFrame(frame);
    else raf = null;
  }

  function start() {
    if (!raf && visible && !reduced && !destroyed) raf = requestAnimationFrame(frame);
  }
  function stop() {
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  }
  function staticFrame() {
    stop();
    frame();
  }

  function size() {
    const r = canvas.getBoundingClientRect();
    W = r.width;
    H = r.height;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    buildMap();
    buildNodes();
    pin = null;
  }

  function onPointerDown(e: PointerEvent) {
    const r = canvas.getBoundingClientRect();
    const cx = e.clientX - r.left,
      cy = e.clientY - r.top;
    pin = { x: cx, y: cy, t0: t };
    if (reduced) {
      pin.t0 = t - 0.5;
      staticFrame();
      clearTimeout(reducedTimer);
      reducedTimer = setTimeout(() => {
        pin = null;
        staticFrame();
      }, 4000);
    } else {
      ripples.push({ x: cx, y: cy, t0: t });
    }
  }

  function onVisibilityChange() {
    visible = !document.hidden;
    if (visible) start();
    else stop();
  }

  let resizeTimer: ReturnType<typeof setTimeout> | undefined;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      size();
      if (reduced) staticFrame();
    }, 180);
  }

  let io: IntersectionObserver | null = null;

  canvas.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("visibilitychange", onVisibilityChange);
  if (typeof IntersectionObserver !== "undefined") {
    io = new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting && !document.hidden;
        if (visible) start();
        else stop();
      },
      { threshold: 0.02 }
    );
    io.observe(canvas);
  }
  window.addEventListener("resize", onResize);

  size();
  if (reduced) {
    lens.x = W * 0.68;
    lens.y = H * 0.46;
    staticFrame();
  } else {
    start();
  }

  return {
    destroy() {
      if (destroyed) return; // idempotent — see docstring
      destroyed = true;
      stop();
      clearTimeout(resizeTimer);
      clearTimeout(reducedTimer);
      canvas.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("resize", onResize);
      io?.disconnect();
    },
  };
}


// Exported for unit tests only (implementation guide §6.2's required test surface: seeded map
// reproducibility). `pinAlphaAt` / `nearestK` / `supportedRangeFromPrices` are already exported
// above at module scope.
export const __testables = { mulberry32 };
